import LiveActivityFeed from '@/components/LiveActivityFeed'
import SocialProofBanner from '@/components/SocialProofBanner'
import SuccessStats from '@/components/SuccessStats'
import TestimonialCarousel from '@/components/TestimonialCarousel'
import UserCountBadge from '@/components/UserCountBadge'
import Features from '@app-studio-web/ui/components/Features'
import FrequentlyAskedQuestions from '@app-studio-web/ui/components/FrequentlyAskedQuestions'
import Plans from '@app-studio-web/ui/components/Plans'
import Trust from '@app-studio-web/ui/components/Trust'
import type { GetPricesData } from '@app-studio-web/ui/loaders/getPricesLoader'
import ErrorPage from '@app-studio-web/ui/pages/ErrorPage'
import LoadingPage from '@app-studio-web/ui/pages/LoadingPage'
import type { Plan } from '@app-studio-web/ui/types'
import { Suspense, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Await, useLoaderData } from 'react-router-dom'

const TRIAL_PERIOD_DAYS = 7

export default function TrialPlansPage() {
  const [countdownUntil, setCountdownUntil] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<Plan>('first')
  const { t } = useTranslation()
  const data = useLoaderData() as GetPricesData

  useEffect(() => {
    setCountdownUntil(Date.now() + 9 * 60 * 1000)
  }, [])

  if (isSubmitting) return <LoadingPage />

  return (
    <Suspense fallback={<LoadingPage />}>
      <Await errorElement={<ErrorPage />} resolve={data}>
        {(getPricesData: GetPricesData) => (
          <main className="custom-container pt-8 pb-14">
            <h1 className="custom-heading mb-4">
              <Trans
                components={{
                  colored: <span className="text-primary" />,
                }}
                i18nKey="pages.checkout.trialPlans.heading"
              />
            </h1>

            <p className="mb-6 text-center font-semibold">
              {t('pages.checkout.trialPlans.subheading', {
                count: TRIAL_PERIOD_DAYS,
              })}
            </p>

            <SocialProofBanner />
            <UserCountBadge />

            <section className="mt-4 mb-4">
              <LiveActivityFeed />
            </section>

            <section className="mb-6">
              <SuccessStats />
            </section>

            <section className="mb-10">
              <Plans
                buttonTitle={t('pages.checkout.trialPlans.startTrial')}
                countdownUntil={countdownUntil}
                currency={getPricesData.currency}
                firstIntroPrice={undefined}
                firstPerk={t('pages.checkout.plans.realtimeSpeakingPerk')}
                firstPrice={getPricesData.firstPrice}
                isSubmitting={isSubmitting}
                onPlanChange={setSelectedPlan}
                secondIntroPrice={undefined}
                secondPrice={undefined}
                selectedPlan={selectedPlan}
                setIsSubmitting={setIsSubmitting}
                showMoneyBackGuarantee={false}
                topIntroPrice={undefined}
                topPrice={undefined}
                trialPeriodDays={TRIAL_PERIOD_DAYS}
              />
            </section>

            <section className="mb-12">
              <TestimonialCarousel />
            </section>

            <section className="mb-12">
              <Trust />
            </section>

            <section className="mb-12">
              <Features />
            </section>

            <section className="mb-12">
              <FrequentlyAskedQuestions />
            </section>

            <section className="mb-16">
              <Plans
                buttonTitle={t('pages.checkout.trialPlans.startTrial')}
                countdownUntil={countdownUntil}
                currency={getPricesData.currency}
                firstIntroPrice={undefined}
                firstPerk={t('pages.checkout.plans.realtimeSpeakingPerk')}
                firstPrice={getPricesData.firstPrice}
                isSubmitting={isSubmitting}
                onPlanChange={setSelectedPlan}
                secondIntroPrice={undefined}
                secondPrice={undefined}
                selectedPlan={selectedPlan}
                setIsSubmitting={setIsSubmitting}
                showMoneyBackGuarantee={false}
                topIntroPrice={undefined}
                topPrice={undefined}
                trialPeriodDays={TRIAL_PERIOD_DAYS}
              />
            </section>
          </main>
        )}
      </Await>
    </Suspense>
  )
}
